<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  getIvrMenu,
  getIvrMenus,
  getIvrTargets,
} from 'dashboard/api/relayVoiceManager';
import IvrMenuEditor from './IvrMenuEditor.vue';

const props = defineProps({
  ivrSet: { type: Object, default: null },
});

const { t } = useI18n();
const { accountId } = useAccount();
const menus = ref([]);
const targets = ref({});
const loading = ref(true);
const failed = ref(false);
const editing = ref(null);

// IVR prompts recorded in the voice callers hear now.
const prompts = computed(() =>
  (props.ivrSet?.slots || [])
    .filter(slot => slot.active?.[props.ivrSet.voice])
    .map(slot => slot.slot)
);

const load = async () => {
  loading.value = true;
  try {
    const [list, destinations] = await Promise.all([
      getIvrMenus(accountId.value),
      getIvrTargets(accountId.value),
    ]);
    menus.value = list.data;
    targets.value = destinations.data;
    failed.value = false;
  } catch (error) {
    failed.value = true;
  } finally {
    loading.value = false;
  }
};

const startNew = () => {
  editing.value = { uuid: null, spec: null };
};
const edit = async menu => {
  try {
    const { data } = await getIvrMenu(accountId.value, menu.ivr_menu_uuid);
    editing.value = { uuid: data.ivr_menu_uuid, spec: data.spec };
  } catch (error) {
    useAlert(t('TEAM_LEAD.GENERIC_ERROR'));
  }
};
const saved = () => {
  editing.value = null;
  load();
};

onMounted(load);
defineExpose({ startNew, editing });
</script>

<template>
  <section
    class="rounded-xl outline outline-1 outline-n-container bg-n-solid-1 p-5 flex flex-col gap-4"
    data-tour="ivr"
  >
    <header class="flex items-start justify-between gap-4 flex-wrap">
      <div class="flex flex-col gap-1 max-w-xl">
        <h2 class="text-base font-semibold text-n-slate-12">
          {{ t('TEAM_LEAD.IVR.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ t('TEAM_LEAD.IVR.DESCRIPTION') }}
        </p>
      </div>
      <Button
        v-if="!editing"
        :label="t('TEAM_LEAD.IVR.NEW')"
        icon="i-lucide-plus"
        size="sm"
        data-tour="ivr-new"
        @click="startNew"
      />
    </header>

    <IvrMenuEditor
      v-if="editing"
      :key="editing.uuid || 'new'"
      :menu-uuid="editing.uuid"
      :spec="editing.spec"
      :targets="targets"
      :prompts="prompts"
      @saved="saved"
      @cancel="editing = null"
    />
    <template v-else>
      <woot-loading-state v-if="loading" :message="t('TEAM_LEAD.LOADING')" />
      <p v-else-if="failed" class="text-sm text-n-amber-11">
        {{ t('TEAM_LEAD.IVR.UNAVAILABLE') }}
      </p>
      <p v-else-if="!menus.length" class="text-sm text-n-slate-11">
        {{ t('TEAM_LEAD.IVR.EMPTY') }}
      </p>
      <ul v-else class="divide-y divide-n-weak border-t border-n-weak">
        <li
          v-for="menu in menus"
          :key="menu.ivr_menu_uuid"
          class="flex items-center justify-between gap-4 py-3"
        >
          <div class="flex flex-col gap-0.5">
            <span class="text-sm font-medium text-n-slate-12">
              {{ menu.name }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{
                t('TEAM_LEAD.IVR.DIAL', {
                  number: menu.extension,
                  status: menu.enabled
                    ? t('TEAM_LEAD.IVR.ON')
                    : t('TEAM_LEAD.IVR.OFF'),
                })
              }}
            </span>
          </div>
          <Button
            v-if="menu.managed_in_relay"
            :label="t('TEAM_LEAD.IVR.EDIT')"
            variant="faded"
            color="slate"
            size="sm"
            @click="edit(menu)"
          />
          <span v-else class="text-xs text-n-slate-10">
            {{ t('TEAM_LEAD.IVR.CONSOLE_ONLY') }}
          </span>
        </li>
      </ul>
      <p class="text-xs text-n-slate-10">
        {{ t('TEAM_LEAD.IVR.ROUTING_NOTE') }}
      </p>
    </template>
  </section>
</template>
